import {
  SurveysApiListQuery,
  SurveysApiListResult,
  SurveysApiRespondentScope,
  SurveysApiResultsPayload,
  SurveysApiSourceFilter,
  SurveysApiStatsAnswer,
  SurveysApiStatsPayload,
  SurveysApiStatsQuestion,
  SurveysApiSurveySummary,
  SurveysApiSuccessEnvelope,
  SurveysBetaAnswerShare,
  SurveysBetaFieldwork,
  SurveysBetaListRow,
  SurveysBetaQuestionResult,
  SurveysBetaResultsViewModel,
} from './surveys-api-v1.types';

export function buildSurveysListQueryParams(
  query: SurveysApiListQuery
): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (query.surveyStatus?.length) {
    params.survey_status = query.surveyStatus.join(',');
  }
  if (query.surveyType?.length) {
    params.survey_type = query.surveyType.join(',');
  }
  if (query.panel?.length) {
    params.panel = query.panel.join(',');
  }
  if (query.brand?.length) {
    params.brand = query.brand.join(',');
  }
  if (query.title?.trim()) {
    params.title = query.title.trim();
  }
  if (query.createdAfter) {
    params.created_after = query.createdAfter;
  }
  if (query.createdBefore) {
    params.created_before = query.createdBefore;
  }
  if (typeof query.limit === 'number') {
    params.limit = query.limit;
  }
  if (query.cursor) {
    params.cursor = query.cursor;
  }
  return params;
}

export function buildResultsQueryParams(query: {
  source?: SurveysApiSourceFilter;
  respondentScope?: SurveysApiRespondentScope;
  breakout?: string | null;
  answerFilter?: string[];
  startDate?: string;
  endDate?: string;
}): Record<string, string> {
  const params: Record<string, string> = {};
  if (query.source && query.source !== 'all') {
    params.source = query.source;
  }
  if (query.respondentScope && query.respondentScope !== 'all') {
    params.respondent_scope = query.respondentScope;
  }
  if (query.breakout) {
    params.breakout = query.breakout;
  }
  if (query.answerFilter?.length) {
    params.answer_filter = query.answerFilter.join(',');
  }
  if (query.startDate) {
    params.start_date = query.startDate;
  }
  if (query.endDate) {
    params.end_date = query.endDate;
  }
  return params;
}

export function adaptSurveyListEnvelope(
  envelope: SurveysApiSuccessEnvelope<SurveysApiSurveySummary[]>
): SurveysApiListResult {
  const items = Array.isArray(envelope.data) ? envelope.data : [];
  return {
    items,
    page: {
      limit: envelope.page?.limit ?? items.length,
      nextCursor: envelope.page?.next_cursor ?? null,
      hasMore: Boolean(envelope.page?.has_more),
    },
  };
}

export function adaptSurveyListRows(
  items: SurveysApiSurveySummary[]
): SurveysBetaListRow[] {
  return items.map((item) => ({
    extSurveyId: item.ext_survey_id,
    title: item.title || 'Untitled',
    surveyStatus: item.survey_status,
    surveyType: item.survey_type ?? null,
    panel: item.panel ?? null,
    brandName: item.brand?.name ?? null,
    startDate: item.start_date ?? null,
    endDate: item.end_date ?? null,
    isPublished: Boolean(item.is_published),
    hasTrends: Boolean(item.has_trends),
    points: item.points ?? 0,
  }));
}

function scopeCount(
  total: number | undefined,
  ai: number | undefined,
  human: number | undefined,
  scope: SurveysApiRespondentScope
): number {
  if (scope === 'ai') {
    return ai ?? 0;
  }
  if (scope === 'human') {
    return human ?? 0;
  }
  if (typeof total === 'number') {
    return total;
  }
  return (ai ?? 0) + (human ?? 0);
}

function answerCountFromDemographics(
  answer: SurveysApiStatsAnswer,
  scope: SurveysApiRespondentScope
): number {
  const demos = answer.demographics;
  if (!demos) {
    return 0;
  }
  let best = 0;
  for (const segments of Object.values(demos)) {
    if (!Array.isArray(segments)) {
      continue;
    }
    const sum = segments.reduce(
      (acc, segment) =>
        acc +
        scopeCount(
          segment.total_users,
          segment.ai_users,
          segment.human_users,
          scope
        ),
      0
    );
    if (sum > best) {
      best = sum;
    }
  }
  return best;
}

function answerRespondentCount(
  answer: SurveysApiStatsAnswer,
  scope: SurveysApiRespondentScope
): number {
  const direct = scopeCount(
    answer.total_users,
    answer.ai_users,
    answer.human_users,
    scope
  );
  if (direct > 0) {
    return direct;
  }
  return answerCountFromDemographics(answer, scope);
}

function questionRespondentCount(
  question: SurveysApiStatsQuestion,
  scope: SurveysApiRespondentScope
): number {
  const direct = scopeCount(
    question.unique_users ?? question.total_users,
    question.unique_ai_users ?? question.ai_users,
    question.unique_human_users ?? question.human_users,
    scope
  );
  if (direct > 0) {
    return direct;
  }
  return question.answers.reduce(
    (max, answer) => Math.max(max, answerRespondentCount(answer, scope)),
    0
  );
}

function asStatsPayload(
  payload: SurveysApiResultsPayload
): SurveysApiStatsPayload | undefined {
  if (payload.raw && typeof payload.raw === 'object') {
    const raw = payload.raw as SurveysApiStatsPayload;
    if (raw.questions || raw.ai_users || raw.human_users) {
      return raw;
    }
  }
  return payload.stats;
}

function normaliseAnswers(
  answers: SurveysApiStatsAnswer[] | Record<string, SurveysApiStatsAnswer> | undefined
): SurveysApiStatsAnswer[] {
  if (!answers) {
    return [];
  }
  if (Array.isArray(answers)) {
    return answers;
  }
  return Object.values(answers);
}

function normaliseQuestions(
  stats: SurveysApiStatsPayload | undefined
): SurveysApiStatsQuestion[] {
  if (!stats?.questions) {
    return [];
  }
  const list = Array.isArray(stats.questions)
    ? stats.questions
    : Object.values(stats.questions as unknown as Record<string, SurveysApiStatsQuestion>);
  return list.map((question) => ({
    ...question,
    answers: normaliseAnswers(question.answers),
  }));
}

function buildAnswerShares(
  question: SurveysApiStatsQuestion,
  scope: SurveysApiRespondentScope,
  totalRespondents: number
): SurveysBetaAnswerShare[] {
  const counted = question.answers.map((answer) => ({
    id: String(answer.id),
    title: answer.title || answer.display_name || 'Untitled answer',
    imageUrl: answer.image_url ?? null,
    count: answerRespondentCount(answer, scope),
  }));
  const denominator = totalRespondents > 0
    ? totalRespondents
    : counted.reduce((sum, row) => sum + row.count, 0);
  const withPercent = counted
    .map((row) => ({
      ...row,
      percent: denominator > 0 ? (row.count / denominator) * 100 : 0,
      isLeading: false,
    }))
    .sort((a, b) => b.count - a.count || a.title.localeCompare(b.title));
  if (withPercent[0]) {
    withPercent[0] = { ...withPercent[0], isLeading: true };
  }
  return withPercent;
}

function buildFieldwork(
  stats: SurveysApiStatsPayload | undefined,
  questions: SurveysBetaQuestionResult[]
): SurveysBetaFieldwork {
  const aiCompleted = stats?.ai_users?.completed ?? 0;
  const humanCompleted = stats?.human_users?.completed ?? 0;
  const starts =
    stats?.total_users?.started ??
    (stats?.ai_users?.started ?? 0) + (stats?.human_users?.started ?? 0);
  const fromBuckets = aiCompleted + humanCompleted;
  const fromQuestions = questions.reduce(
    (max, question) => Math.max(max, question.totalRespondents),
    0
  );
  const completes = stats?.total_users?.completed ?? (fromBuckets || fromQuestions);
  const completionRate =
    starts > 0 ? completes / starts : completes > 0 ? 1 : null;

  const firstWithSample = questions.find((question) => question.aiIcc != null);
  return {
    completes,
    starts: starts || completes,
    completionRate,
    aiCompleted,
    humanCompleted,
    rawN: stats?.ai_users?.raw_n ?? firstWithSample?.aiRawN ?? null,
    effectiveN: stats?.ai_users?.effective_n ?? firstWithSample?.aiEffectiveN ?? null,
    icc: stats?.ai_users?.icc ?? firstWithSample?.aiIcc ?? null,
  };
}

export function adaptResultsToViewModel(
  payload: SurveysApiResultsPayload,
  options?: {
    respondentScope?: SurveysApiRespondentScope;
    definitionTitle?: string;
  }
): SurveysBetaResultsViewModel {
  const scope = options?.respondentScope ?? 'all';
  const stats = asStatsPayload(payload);
  const questionsRaw = normaliseQuestions(stats);
  const questions: SurveysBetaQuestionResult[] = questionsRaw.map((question) => {
    const totalRespondents = questionRespondentCount(question, scope);
    return {
      id: String(question.id),
      title: question.title || 'Untitled question',
      type: question.type || question.question_type || 'single_select',
      answered: totalRespondents,
      totalRespondents,
      aiRawN: question.ai_raw_n ?? null,
      aiEffectiveN: question.ai_effective_n ?? null,
      aiIcc: question.ai_icc ?? null,
      answers: buildAnswerShares(question, scope, totalRespondents),
    };
  });

  const suppressionNotes = (payload.suppression?.entries || [])
    .map((entry) => entry.reason || entry.segment_label || entry.attribute_key || '')
    .filter(Boolean);

  const fieldwork = buildFieldwork(stats, questions);
  // Prefer survey-level AI/human completed when present; else derive from first question.
  if (!stats?.ai_users?.completed && questions[0]) {
    const first = questionsRaw[0];
    fieldwork.aiCompleted = first?.unique_ai_users ?? first?.ai_users ?? 0;
    fieldwork.humanCompleted = first?.unique_human_users ?? first?.human_users ?? 0;
  }

  return {
    extSurveyId: payload.ext_survey_id,
    title: options?.definitionTitle || stats?.title || 'Survey',
    questions,
    fieldwork,
    suppressionApplied: Boolean(payload.suppression?.applied),
    suppressionNotes,
    lastAnswerAt: payload.last_answer_at ?? null,
  };
}

export function isEditableSurveyStatus(status: string): boolean {
  const normalised = status.toLowerCase();
  return normalised === 'draft' || normalised === 'building' || normalised === 'rejected';
}

export function formatSurveyStatusLabel(status: string): string {
  if (!status) {
    return 'Unknown';
  }
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}
